/*
 * gift-card-recipient-form.js
 * ---------------------------------------------------------------------------
 * Formulario de destinatario para productos gift card (Thoson, sep 2026).
 *
 * Escribe las line item properties que Shopify espera al comprar una gift card
 * para otra persona:
 *
 *   properties[__shopify_send_gift_card_to_recipient] = "true"
 *   properties[Recipient email]   (obligatoria)
 *   properties[Recipient name]    (opcional, max 255)
 *   properties[Message]           (opcional, max 200)
 *   properties[Send on]           (opcional, yyyy-mm-dd, hasta 90 dias vista)
 *   properties[__shopify_offset]  (opcional, minutos de desfase horario)
 *
 * Se engancha al contador data-required-fields / data-valid-fields que el tema
 * usa en #ProductSubmitButton-<section>, igual que <custom-product-field>:
 *   - suma 1 a requiredFields al conectar,
 *   - suma o resta 1 a validFields SOLO en transiciones valido<->invalido.
 * Como el modo regalo arranca apagado, el elemento nace valido y por eso suma
 * tambien 1 a validFields al conectar.
 *
 * Los inputs ocultos con valor viven dentro de .gpo-hidden-inputs: asi viajan
 * tanto por la ruta normal (FormData del <form>) como por la ruta multi-item
 * del tema, que reescribe esos inputs como items[N][properties][...].
 * Los vacios se aparcan fuera y deshabilitados.
 */
(function () {
  'use strict';

  if (customElements.get('gift-card-recipient-form')) return;

  var MAX_DAYS_AHEAD = 90;
  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

  /* El tema construye el HTML del modo multi-item concatenando value="..." en
     crudo, asi que una comilla doble partiria el input. Las convertimos en
     comillas tipograficas: se ve mejor y no rompe nada. */
  function smartQuotes(value) {
    var open = true;
    return value.replace(/"/g, function () {
      var ch = open ? '“' : '”';
      open = !open;
      return ch;
    });
  }

  function pad(n) {
    return n < 10 ? '0' + n : String(n);
  }

  function toISODate(d) {
    return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate());
  }

  customElements.define(
    'gift-card-recipient-form',
    class GiftCardRecipientForm extends HTMLElement {
      connectedCallback() {
        if (this.ready) return;
        this.ready = true;

        this.sectionId = this.dataset.section;
        this.productForm =
          this.closest('form') || document.getElementById('product-form-' + this.sectionId);
        if (!this.productForm) return;

        this.mainAtcButton = document.getElementById('ProductSubmitButton-' + this.sectionId);
        this.btnLabel = this.mainAtcButton && this.mainAtcButton.querySelector('.main-atc__label');
        this.btnError = this.mainAtcButton && this.mainAtcButton.querySelector('.main-atc__error');
        this.stickyBtn = document.getElementById('sticky-atc-' + this.sectionId);
        this.stickyLabel = this.stickyBtn && this.stickyBtn.querySelector('.sticky-atc__label');
        this.stickyError = this.stickyBtn && this.stickyBtn.querySelector('.sticky-atc__error');
        this.atcButtons = document.querySelectorAll('.main-product-atc');

        this.toggle = this.querySelector('[data-gcr="toggle"]');
        this.fieldsBox = this.querySelector('[data-gcr="fields"]');
        this.selfNote = this.querySelector('[data-gcr="self-note"]');
        this.emailInput = this.querySelector('[data-gcr="email"]');
        this.nameInput = this.querySelector('[data-gcr="name"]');
        this.messageInput = this.querySelector('[data-gcr="message"]');
        this.dateInput = this.querySelector('[data-gcr="date"]');
        this.counter = this.querySelector('[data-gcr="counter"]');
        this.activeBin = this.querySelector('[data-gcr="active-bin"]');
        this.parkedBin = this.querySelector('[data-gcr="parked-bin"]');

        this.propInputs = {
          flag: this.querySelector('[data-gcr-prop="flag"]'),
          email: this.querySelector('[data-gcr-prop="email"]'),
          name: this.querySelector('[data-gcr-prop="name"]'),
          message: this.querySelector('[data-gcr-prop="message"]'),
          date: this.querySelector('[data-gcr-prop="date"]'),
          offset: this.querySelector('[data-gcr-prop="offset"]')
        };

        this.removeAttribute('hidden');
        this.atcErrorMsg = this.dataset.atcErrorMsg || 'Add their email first';
        this.setDateBounds();

        /* Registro en el contador del tema. Nace valido (modo regalo apagado),
           asi que sumamos en los dos contadores para no dejar el boton bloqueado. */
        this.isValid = true;
        if (this.mainAtcButton) {
          var req = parseInt(this.mainAtcButton.dataset.requiredFields || '0', 10) + 1;
          var val = parseInt(this.mainAtcButton.dataset.validFields || '0', 10) + 1;
          this.mainAtcButton.dataset.requiredFields = req;
          this.mainAtcButton.dataset.validFields = val;
          if (this.btnError) this.btnError.innerHTML = this.atcErrorMsg;
          if (this.stickyError) this.stickyError.innerHTML = this.atcErrorMsg;
        }

        this.onToggle = this.onToggle.bind(this);
        this.onInput = this.onInput.bind(this);
        this.onSubmitCapture = this.onSubmitCapture.bind(this);

        if (this.toggle) this.toggle.addEventListener('change', this.onToggle);
        [this.emailInput, this.nameInput, this.messageInput, this.dateInput].forEach(
          function (el) {
            if (!el) return;
            el.addEventListener('input', this.onInput);
            el.addEventListener('change', this.onInput);
          }.bind(this)
        );

        /* Capture en document: <product-form> escucha el submit sobre el propio
           <form>, y en fase target el orden es el de registro, asi que un
           listener en el form no nos garantizaria ir primero. En document si. */
        document.addEventListener('submit', this.onSubmitCapture, true);

        this.sync();
      }

      setDateBounds() {
        if (!this.dateInput) return;
        var today = new Date();
        var max = new Date();
        max.setDate(max.getDate() + MAX_DAYS_AHEAD);
        this.dateInput.min = toISODate(today);
        this.dateInput.max = toISODate(max);
      }

      get sendAsGift() {
        return !!(this.toggle && this.toggle.checked);
      }

      onToggle() {
        var gift = this.sendAsGift;
        if (this.fieldsBox) this.fieldsBox.hidden = !gift;
        if (this.selfNote) this.selfNote.hidden = gift;
        this.sync();
        if (gift && this.emailInput) this.emailInput.focus();
      }

      onInput(event) {
        if (this.counter && this.messageInput && event.target === this.messageInput) {
          this.counter.textContent = this.messageInput.value.length + '/200';
        }
        this.sync();
      }

      onSubmitCapture(event) {
        if (event.target !== this.productForm) return;
        this.sync();
        if (this.isValid) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        this.showFieldError(true);
        if (this.emailInput) this.emailInput.focus();
      }

      sync() {
        var gift = this.sendAsGift;
        var email = this.emailInput ? this.emailInput.value.trim() : '';
        var emailOk = EMAIL_RE.test(email);
        var send = gift && emailOk;
        var date = this.dateInput ? this.dateInput.value : '';

        this.place(this.propInputs.flag, send, 'true');
        this.place(this.propInputs.email, send, email);
        this.place(this.propInputs.name, send, smartQuotes(this.val(this.nameInput)));
        this.place(this.propInputs.message, send, smartQuotes(this.val(this.messageInput)));
        this.place(this.propInputs.date, send, date);
        this.place(this.propInputs.offset, send && !!date, String(new Date().getTimezoneOffset()));

        this.showFieldError(gift && email.length > 0 && !emailOk);
        this.setValidity(!gift || emailOk);
      }

      val(el) {
        return el ? el.value.trim() : '';
      }

      place(input, active, value) {
        if (!input) return;
        var on = !!active && value !== '' && value != null;
        input.value = on ? value : '';
        input.disabled = !on;
        var bin = on ? this.activeBin : this.parkedBin;
        if (bin && input.parentNode !== bin) bin.appendChild(input);
      }

      /* Misma disciplina que <custom-product-field>: solo actuamos en las
         transiciones, nunca en cada pulsacion, o el contador se descuadra. */
      setValidity(ok) {
        if (ok === this.isValid) return;
        this.isValid = ok;
        if (this.mainAtcButton) {
          var n = parseInt(this.mainAtcButton.dataset.validFields || '0', 10) + (ok ? 1 : -1);
          this.mainAtcButton.dataset.validFields = n;
        }
        this.applyButtonState();
      }

      applyButtonState() {
        var btn = this.mainAtcButton;
        if (!btn) return;
        var allValid = btn.dataset.validFields === btn.dataset.requiredFields;
        var unavailable = btn.dataset.unavailable === 'true';
        Array.prototype.forEach.call(this.atcButtons, function (b) {
          if (allValid && !unavailable) b.removeAttribute('disabled');
          else b.setAttribute('disabled', '');
        });
        this.swap(this.btnLabel, this.btnError, allValid);
        this.swap(this.stickyLabel, this.stickyError, allValid);
      }

      swap(label, error, allValid) {
        if (label) label.style.display = allValid ? '' : 'none';
        if (error) error.style.display = allValid ? 'none' : '';
      }

      showFieldError(show) {
        if (!this.emailInput) return;
        this.emailInput.classList.toggle('input--error', !!show);
        var msg = this.querySelector('[data-gcr="email-error"]');
        if (msg) msg.hidden = !show;
      }
    }
  );
})();
